(() => {
  const initialize = (root) => {
    if (!root || root.dataset.dmXrInitialized === 'true') return;

    const track = root.querySelector('[data-dm-xr-track]');
    const slides = Array.from(root.querySelectorAll('[data-dm-xr-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-dm-xr-dot]'));
    const previous = root.querySelector('[data-dm-xr-previous]');
    const next = root.querySelector('[data-dm-xr-next]');
    const revealAnchor = root.querySelector('.dm-featured-xr__media');

    if (!track || slides.length === 0) return;

    root.dataset.dmXrInitialized = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = 0;
    let scrollTicking = false;
    let dragging = false;
    let dragged = false;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let settleTimer = 0;
    let resizeTimer = 0;
    let revealTicking = false;
    let lastVisibleRatio = 0;

    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('id');
      clone.classList.remove('is-active');
      clone.classList.add('is-clone');
      clone.removeAttribute('data-dm-xr-slide');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
      return clone;
    };

    if (slides.length > 1) {
      track.prepend(makeClone(slides[slides.length - 1]));
      track.append(makeClone(slides[0]));
    }

    const pages = Array.from(track.querySelectorAll('.dm-featured-xr__slide'));
    const hasLoop = slides.length > 1;
    const headers = slides.map((slide) => slide.querySelector('.dm-featured-xr__header')).filter(Boolean);

    const syncHeaderHeight = () => {
      root.style.removeProperty('--dm-xr-header-height');
      const height = Math.max(0, ...headers.map((header) => header.offsetHeight));
      root.style.setProperty('--dm-xr-header-height', `${height}px`);
    };

    const setActive = (index) => {
      activeIndex = ((index % slides.length) + slides.length) % slides.length;
      pages.forEach((page, pageIndex) => page.classList.toggle('is-active', realIndexForPage(pageIndex) === activeIndex));
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === activeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (previous) previous.disabled = slides.length < 2;
      if (next) next.disabled = slides.length < 2;
    };

    const nearestPageIndex = () => {
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      pages.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
        if (distance < nearestDistance) {
          nearest = index;
          nearestDistance = distance;
        }
      });
      return nearest;
    };

    const realIndexForPage = (pageIndex) => {
      if (!hasLoop) return Math.max(0, Math.min(pageIndex, slides.length - 1));
      return ((pageIndex - 1) % slides.length + slides.length) % slides.length;
    };

    const jumpToPage = (pageIndex) => {
      if (!pages[pageIndex]) return;
      const previousBehavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = pages[pageIndex].offsetLeft;
      track.style.scrollBehavior = previousBehavior;
    };

    const normalizeLoop = () => {
      if (!hasLoop) return;
      const pageIndex = nearestPageIndex();
      if (pageIndex === 0) jumpToPage(slides.length);
      if (pageIndex === pages.length - 1) jumpToPage(1);
    };

    const scheduleNormalize = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(normalizeLoop, reducedMotion ? 0 : 140);
    };

    const goToPage = (pageIndex) => {
      const targetPage = Math.max(0, Math.min(pageIndex, pages.length - 1));
      track.scrollTo({
        left: pages[targetPage].offsetLeft,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
      setActive(realIndexForPage(targetPage));
      scheduleNormalize();
    };

    const goTo = (index) => {
      const targetIndex = ((index % slides.length) + slides.length) % slides.length;
      goToPage(hasLoop ? targetIndex + 1 : targetIndex);
    };

    const goBy = (direction) => {
      if (!hasLoop) {
        goTo(activeIndex + direction);
        return;
      }

      if (direction > 0 && activeIndex === slides.length - 1) {
        goToPage(pages.length - 1);
        return;
      }

      if (direction < 0 && activeIndex === 0) {
        goToPage(0);
        return;
      }

      goTo(activeIndex + direction);
    };

    track.addEventListener('scroll', () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        setActive(realIndexForPage(nearestPageIndex()));
        scheduleNormalize();
        scrollTicking = false;
      });
    }, { passive: true });

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goBy(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goBy(1);
      }
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragged = false;
      if (event.target.closest('a, button, input, select, textarea, label, summary, [contenteditable], [role="button"], [role="link"]')) return;
      dragging = true;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - dragStartX;
      if (Math.abs(delta) > 6) dragged = true;
      track.scrollLeft = dragStartScroll - delta;
    });

    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      goToPage(nearestPageIndex());
    };

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('click', (event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, true);

    if (previous) previous.addEventListener('click', () => goBy(-1));
    if (next) next.addEventListener('click', () => goBy(1));
    dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.index))));

    if ('onscrollend' in window) track.addEventListener('scrollend', normalizeLoop);

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        syncHeaderHeight();
        jumpToPage(hasLoop ? activeIndex + 1 : activeIndex);
        requestRevealUpdate();
      }, 100);
    }, { passive: true });

    const revealEnabled = root.dataset.dmXrReveal !== 'false';
    const clamp = (value) => Math.max(0, Math.min(1, value));

    function updateReveal() {
      if (!revealEnabled || reducedMotion) {
        root.classList.add('is-revealed');
        return;
      }

      const bounds = root.getBoundingClientRect();
      const anchorBounds = revealAnchor ? revealAnchor.getBoundingClientRect() : bounds;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleHeight = Math.max(0, Math.min(bounds.bottom, viewportHeight) - Math.max(bounds.top, 0));
      const visibleRatio = clamp(visibleHeight / Math.min(bounds.height, viewportHeight));
      const movingIntoView = visibleRatio >= lastVisibleRatio;

      if (movingIntoView && anchorBounds.top <= viewportHeight * .84 && anchorBounds.bottom >= viewportHeight * .12) {
        root.classList.add('is-revealed');
      }

      if (!movingIntoView && anchorBounds.top >= viewportHeight * .9) {
        root.classList.remove('is-revealed');
      }

      lastVisibleRatio = visibleRatio;
    }

    const requestRevealUpdate = () => {
      if (revealTicking) return;
      revealTicking = true;
      requestAnimationFrame(() => {
        updateReveal();
        revealTicking = false;
      });
    };

    if (revealEnabled) {
      if (!reducedMotion) {
        window.addEventListener('scroll', requestRevealUpdate, { passive: true });
      }
      root.classList.add('is-motion-ready');
    }

    syncHeaderHeight();
    if (document.fonts) document.fonts.ready.then(syncHeaderHeight);
    setActive(0);
    requestAnimationFrame(() => {
      if (hasLoop) jumpToPage(1);
      updateReveal();
    });
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-dm-featured-xr]').forEach(initialize);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
